import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonList,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    sentence: string;
    options: string[];
    correctOption: string;
}


const ActionWords: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctOption) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                if (currentIndex != questions.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setSelectedOption(null);
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'ActionWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/ActionWords.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Action Words Worksheets for UKG | Learn Verbs Through Fun Activities"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore fun and interactive exercises on action words (verbs) for UKG kids at Mama’s Guidance to boost their vocabulary and understanding of everyday activities.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Action Words</h1>

                                <p>
                                    <strong>Action words</strong> tell us what someone or something is doing. They are also called <strong>verbs</strong> and are a vital part of building basic sentences. Our UKG-level action word worksheets help children recognize common actions and use them in the right context.
                                </p>

                                <h2>🏃‍♂️ What Are Action Words?</h2>
                                <p>
                                    Action words describe something that is happening — like <b><em>run</em>, <em>eat</em>, <em>jump</em>, or <em>play</em></b>. They help children explain what people or animals are doing.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify common action words</li>
                                    <li>Use action words in simple sentences</li>
                                    <li>Recognize actions in pictures</li>
                                    <li>Improve sentence formation and vocabulary</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Match the action word with its picture</li>
                                    <li>Circle the action word in a sentence</li>
                                    <li>Fill in the blanks using correct action words</li>
                                    <li>Choose the correct verb from a word bank</li>
                                    <li>Identify <strong>“doing words”</strong> in short paragraphs</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Act out actions and let the child guess the word</li>
                                    <li>Use picture books to point out what each character is doing</li>
                                    <li>Ask questions like: <strong>“What is the boy doing?” or “Can you show me jump?”</strong></li>
                                    <li>Encourage speaking in complete sentences using action words</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Turn everyday activities into learning moments! Ask your child to describe their actions like <strong>“I am brushing,” “I am walking,”</strong> etc.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Action Words</h1>

                            <p>
                                <strong>Action words</strong> tell us what someone or something is doing. They are also called <strong>verbs</strong> and are a vital part of building basic sentences. Our UKG-level action word worksheets help children recognize common actions and use them in the right context.
                            </p>

                            <h2>🏃‍♂️ What Are Action Words?</h2>
                            <p>
                                Action words describe something that is happening — like <b><em>run</em>, <em>eat</em>, <em>jump</em>, or <em>play</em></b>. They help children explain what people or animals are doing.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify common action words</li>
                                <li>Use action words in simple sentences</li>
                                <li>Recognize actions in pictures</li>
                                <li>Improve sentence formation and vocabulary</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Match the action word with its picture</li>
                                <li>Circle the action word in a sentence</li>
                                <li>Fill in the blanks using correct action words</li>
                                <li>Choose the correct verb from a word bank</li>
                                <li>Identify <strong>“doing words”</strong> in short paragraphs</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Act out actions and let the child guess the word</li>
                                <li>Use picture books to point out what each character is doing</li>
                                <li>Ask questions like: <strong>“What is the boy doing?” or “Can you show me jump?”</strong></li>
                                <li>Encourage speaking in complete sentences using action words</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Turn everyday activities into learning moments! Ask your child to describe their actions like <strong>“I am brushing,” “I am walking,”</strong> etc.
                            </p>
                        </article>
                        <h2>Fill in the Blanks with Correct Action Words</h2>

                        <p>
                            <strong>
                                Q. {questions[currentIndex]?.sentence}
                            </strong>
                        </p>
                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value)}
                        >
                            {questions[currentIndex]?.options.map((option, index) => (
                                <IonItem
                                    className={` ${selectedOption === option
                                        ? option === questions[currentIndex]?.correctOption
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonLabel>{option}</IonLabel>
                                    <IonRadio slot="start" value={option} />
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default ActionWords;
